import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import type { EquipmentType } from '@jobos/shared';
import { computeUnitDays, makeCustomUnitTypeKey } from '@jobos/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { dateToLocalISO } from '@/lib/format';
import type { CustomUnitSpec } from './equipment-helpers';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

interface InUseSlot {
  assetTag: string;
  location: string | null;
}

export interface AddUnitInput {
  typeKey: string;
  /**
   * Set when the user typed the equipment in rather than picking it from the
   * catalog. Null for catalog units.
   */
  custom: CustomUnitSpec | null;
  assetTag: string;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  days: number | null;
}

export interface AddUnitPopoverProps {
  types: EquipmentType[];
  inUseByType: Record<string, InUseSlot[]>;
  onAdd: (input: AddUnitInput) => void;
  disabled?: boolean;
}

interface SlotChoice {
  typeKey: string;
  typeLabel: string;
  setupFee: number;
  dailyRate: number;
  minDays: number;
  assetTag: string;
  inUse: boolean;
  location: string | null;
}

function buildSlots(
  types: EquipmentType[],
  inUseByType: Record<string, InUseSlot[]>,
): SlotChoice[] {
  const result: SlotChoice[] = [];
  for (const t of [...types].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const inUse = new Map(
      (inUseByType[t.key] ?? []).map((s) => [s.assetTag, s.location]),
    );
    for (let i = 1; i <= t.qty; i++) {
      const tag = `${t.idPrefix}${i}`;
      result.push({
        typeKey: t.key,
        typeLabel: t.label,
        setupFee: Number(t.setupFee),
        dailyRate: Number(t.dailyRate),
        minDays: t.minDays,
        assetTag: tag,
        inUse: inUse.has(tag),
        location: inUse.get(tag) ?? null,
      });
    }
  }
  return result;
}

export function AddUnitPopover({
  types,
  inUseByType,
  onAdd,
  disabled = false,
}: AddUnitPopoverProps) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<'catalog' | 'custom'>('catalog');
  const [customLabel, setCustomLabel] = useState('');
  const [customSetupFee, setCustomSetupFee] = useState('');
  const [customDailyRate, setCustomDailyRate] = useState('');
  const slots = useMemo(() => buildSlots(types, inUseByType), [types, inUseByType]);
  const firstAvailable = slots.find((s) => !s.inUse);
  const [selected, setSelected] = useState<string | null>(
    firstAvailable ? `${firstAvailable.typeKey}::${firstAvailable.assetTag}` : null,
  );
  const [location, setLocation] = useState('');
  const [mode, setMode] = useState<'dates' | 'days'>('dates');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [days, setDays] = useState('');

  const reset = () => {
    const next = slots.find((s) => !s.inUse);
    setSelected(next ? `${next.typeKey}::${next.assetTag}` : null);
    setSource('catalog');
    setCustomLabel('');
    setCustomSetupFee('');
    setCustomDailyRate('');
    setLocation('');
    setMode('dates');
    setStartDate('');
    setEndDate('');
    setDays('');
  };

  useEffect(() => {
    if (!open) return;
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const catalogSlot = useMemo(
    () => (selected ? slots.find((s) => `${s.typeKey}::${s.assetTag}` === selected) ?? null : null),
    [selected, slots],
  );

  const nonNegative = (raw: string): number => {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const customSpec = useMemo<CustomUnitSpec | null>(() => {
    const label = customLabel.trim();
    if (!label) return null;
    // Custom equipment has no minimum-days rule — that stays a catalog
    // concept, configured per type in settings.
    return {
      label,
      setupFee: nonNegative(customSetupFee),
      dailyRate: nonNegative(customDailyRate),
      minDays: 0,
    };
  }, [customLabel, customSetupFee, customDailyRate]);

  // Whichever source is active, the rest of the popover (period, preview,
  // submit) reads the same shape.
  const selectedSlot = useMemo<
    Pick<SlotChoice, 'setupFee' | 'dailyRate' | 'minDays'> | null
  >(() => {
    if (source === 'custom') {
      if (!customSpec) return null;
      return {
        setupFee: customSpec.setupFee,
        dailyRate: customSpec.dailyRate,
        minDays: customSpec.minDays,
      };
    }
    return catalogSlot;
  }, [source, customSpec, catalogSlot]);

  const previewDays = useMemo(() => {
    if (!selectedSlot) return 0;
    if (mode === 'days') {
      if (days.trim() === '') return 0;
      const parsed = Number(days);
      if (!Number.isFinite(parsed) || parsed < 0) return 0;
      return computeUnitDays({
        startDate: null,
        endDate: null,
        minDays: selectedSlot.minDays,
        days: parsed,
      });
    }
    if (!startDate || !endDate) return 0;
    return computeUnitDays({ startDate, endDate, minDays: selectedSlot.minDays });
  }, [selectedSlot, mode, days, startDate, endDate]);

  const previewTotal = useMemo(() => {
    if (!selectedSlot) return 0;
    return selectedSlot.setupFee + previewDays * selectedSlot.dailyRate;
  }, [selectedSlot, previewDays]);

  const canSubmit =
    source === 'custom' ? customSpec != null : catalogSlot != null && !catalogSlot.inUse;

  const handleAdd = () => {
    if (!canSubmit) return;
    const isDays = mode === 'days';
    const parsedDays = Number(days);
    // A custom unit gets its own key so it never merges with another unit and
    // never collides with a catalog type.
    const identity =
      source === 'custom' && customSpec
        ? { typeKey: makeCustomUnitTypeKey(crypto.randomUUID()), assetTag: '', custom: customSpec }
        : { typeKey: catalogSlot!.typeKey, assetTag: catalogSlot!.assetTag, custom: null };
    onAdd({
      ...identity,
      location: location.trim() || null,
      startDate: isDays ? null : startDate || null,
      endDate: isDays ? null : endDate || null,
      days: isDays
        ? Number.isFinite(parsedDays) && parsedDays >= 0
          ? parsedDays
          : 0
        : null,
    });
    setOpen(false);
  };

  // Build optgroups
  const groupedByType = useMemo(() => {
    const map = new Map<string, SlotChoice[]>();
    for (const slot of slots) {
      if (!map.has(slot.typeKey)) map.set(slot.typeKey, []);
      map.get(slot.typeKey)!.push(slot);
    }
    return Array.from(map.entries());
  }, [slots]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="h-8 text-sm text-emerald-700 hover:text-emerald-800"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add unit
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[440px] p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold">Add equipment unit</h4>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            {(['catalog', 'custom'] as const).map((sourceOption) => (
              <button
                key={sourceOption}
                type="button"
                onClick={() => setSource(sourceOption)}
                aria-pressed={source === sourceOption}
                className={
                  'px-2 py-0.5 text-[11px] capitalize transition-colors ' +
                  (source === sourceOption
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50')
                }
              >
                {sourceOption}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {source === 'custom' ? (
            <>
              <div>
                <label
                  htmlFor="custom-equipment-name"
                  className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1"
                >
                  Equipment name
                </label>
                <Input
                  id="custom-equipment-name"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  placeholder="e.g. Air scrubber"
                  className="h-8 text-sm"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  One-off for this job — not saved to the equipment settings.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label
                    htmlFor="custom-equipment-setup"
                    className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1"
                  >
                    Setup fee
                  </label>
                  <Input
                    id="custom-equipment-setup"
                    type="number"
                    min="0"
                    step="1"
                    value={customSetupFee}
                    onChange={(e) => setCustomSetupFee(e.target.value)}
                    placeholder="0"
                    className="h-8 text-sm tabular-nums"
                  />
                </div>
                <div>
                  <label
                    htmlFor="custom-equipment-daily"
                    className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1"
                  >
                    Daily rate
                  </label>
                  <Input
                    id="custom-equipment-daily"
                    type="number"
                    min="0"
                    step="1"
                    value={customDailyRate}
                    onChange={(e) => setCustomDailyRate(e.target.value)}
                    placeholder="0"
                    className="h-8 text-sm tabular-nums"
                  />
                </div>
              </div>
            </>
          ) : (
          <div>
            <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">
              Asset ID
            </label>
            <select
              size={9}
              value={selected ?? ''}
              onChange={(e) => setSelected(e.target.value || null)}
              className="w-full text-sm border rounded-md px-2 py-1 bg-white"
              aria-label="Asset ID"
            >
              {groupedByType.map(([typeKey, typeSlots]) => {
                const t = typeSlots[0];
                if (!t) return null;
                const minHint = t.minDays > 0 ? ` · min ${t.minDays} day` : '';
                return (
                  <optgroup
                    key={typeKey}
                    label={`${t.typeLabel} (setup ${money(t.setupFee)} + ${money(t.dailyRate)}/day${minHint})`}
                  >
                    {typeSlots.map((s) => (
                      <option
                        key={s.assetTag}
                        value={`${s.typeKey}::${s.assetTag}`}
                        disabled={s.inUse}
                      >
                        {s.assetTag}
                        {s.inUse ? ` — in use${s.location ? ` (${s.location})` : ''}` : ''}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">
              In-use IDs are disabled. Type fills automatically.
            </p>
          </div>
          )}

          <div>
            <label
              htmlFor="equipment-unit-location"
              className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1"
            >
              Location
            </label>
            <Input
              id="equipment-unit-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Kitchen, Bath 1"
              className="h-8 text-sm"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[11px] uppercase tracking-wide text-gray-500">
                Rental period
              </label>
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                {(['dates', 'days'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={
                      'px-2 py-0.5 text-[11px] capitalize transition-colors ' +
                      (mode === m
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50')
                    }
                    aria-pressed={mode === m}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {mode === 'days' ? (
              <Input
                type="number"
                min="0"
                step="1"
                value={days}
                onChange={(e) => setDays(e.target.value)}
                placeholder="Estimated days"
                aria-label="Days"
                className="h-8 text-sm"
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                    Start
                  </label>
                  <DatePicker
                    aria-label="Start"
                    value={startDate || null}
                    onChange={(d) => setStartDate(d ? dateToLocalISO(d) : '')}
                    placeholder="Optional"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                    End
                  </label>
                  <DatePicker
                    aria-label="End"
                    value={endDate || null}
                    onChange={(d) => setEndDate(d ? dateToLocalISO(d) : '')}
                    placeholder="Optional"
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {selectedSlot && (
            <div className="text-xs text-gray-600 bg-emerald-50/50 rounded px-2 py-1.5 flex items-center justify-between">
              <span>
                {previewDays > 0
                  ? `${previewDays} days × ${money(selectedSlot.dailyRate)} + ${money(selectedSlot.setupFee)} setup`
                  : `${money(selectedSlot.setupFee)} setup (${mode === 'days' ? 'no days yet' : 'no dates yet'})`}
              </span>
              <span className="text-emerald-700 font-semibold tabular-nums">
                {money(previewTotal)}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={!canSubmit}>
              Add
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
